import { z } from 'zod';
import { LIMITES, VERSAO_PROTOCOLO } from './limites';

/**
 * Envelope único de toda mensagem em tempo real. `v` é a versão do protocolo:
 * o receptor recusa versões que não conhece em vez de adivinhar.
 */
export const EnvelopeSchema = z.object({
  v: z.literal(VERSAO_PROTOCOLO),
  id: z.string().min(16),
  tipo: z.string().min(1),
  ts: z.iso.datetime(),
  carga: z.unknown(),
});
export type Envelope = z.infer<typeof EnvelopeSchema>;

/** Identificador de um fluxo replayável: sessao:<id> ou gravacao:<id>. */
export const FluxoSchema = z
  .string()
  .regex(/^(sessao|gravacao):[A-Za-z0-9_-]{1,64}$/, 'fluxo deve ser sessao:<id> ou gravacao:<id>');
export type Fluxo = z.infer<typeof FluxoSchema>;

/**
 * `seq` é inteiro de 32 bits com sinal, nunca BigInt: JSON não transporta
 * BigInt e o painel usa o valor como cursor.
 */
export const SeqSchema = z.number().int().nonnegative().max(2_147_483_647);

export const ReplayavelSchema = z.object({
  fluxo: FluxoSchema,
  seq: SeqSchema,
});
export type Replayavel = z.infer<typeof ReplayavelSchema>;

export const AssinarSchema = z.object({
  fluxos: z
    .array(z.object({ fluxo: FluxoSchema, cursor: SeqSchema.default(0) }))
    .min(1)
    .max(20),
});
export type Assinar = z.infer<typeof AssinarSchema>;

export const ErroLoteSchema = z.enum(['TRANSITORIO', 'PROTOCOLO', 'ESCOPO']);
export type ErroLote = z.infer<typeof ErroLoteSchema>;

/**
 * Ack de um lote. TRANSITORIO manda o daemon reenviar o MESMO lote; os outros
 * dois mandam a carga para a quarentena e avançam o offset.
 */
export const AckLoteSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    ultimoSeq: SeqSchema,
    offsets: z
      .array(z.object({ arquivo: z.string(), offset: z.number().int().nonnegative() }))
      .default([]),
  }),
  z.object({
    ok: z.literal(false),
    erro: ErroLoteSchema,
    mensagem: z.string().max(500).optional(),
  }),
]);
export type AckLote = z.infer<typeof AckLoteSchema>;

export function criarEnvelope<T>(tipo: string, carga: T, agora: Date, id: string): Envelope {
  return { v: VERSAO_PROTOCOLO, id, tipo, ts: agora.toISOString(), carga };
}

export { LIMITES, VERSAO_PROTOCOLO };
